import React, { useState, useEffect, useRef } from "react";
import { isIOS } from "react-device-detect";
import { navigate } from "gatsby";
import { Storage } from "../../../../utils/storage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import isAfter from "date-fns/isAfter";
import addDays from "date-fns/addDays";
import isDate from "date-fns/isDate";
import startOfToday from "date-fns/startOfToday";
import moment from "moment";
import SearchAutoComplete from "../SearchAutoComplete/SearchAutoComplete";
import StayDates from "../StayDates/StayDates";
import GlobalOccupancy from "../GlobalOccupancy/GlobalOccupancy";
import {
  AUTO_LABEL,
  LRR_LABEL,
  FIND_HOTEL_BUTTON_LABEL,
  GROUP_LABEL,
  IGlobalSearchProps,
  PROMO_LABEL,
  RATES_LABEL,
  UPDATE_BUTTON_LABEL,
  PACKAGE_LABEL,
} from "./GlobalSearchProps";
import { useFocus } from "../../../../hooks/useFocus";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { ICoordinates } from "../../../../@types/ICoordinates";
import IBrand from "../../../../@types/IBrand";
import { Constants } from "../../../../@types/Constants";

import { GlobalSearchForm } from "./GlobalSearchHelperComponents/GlobalSearchForm/GlobalSearchForm";
import {
  SearchForm,
  Location,
  Dates,
  OccupancyText,
  useClickOutSide,
} from "./GlobalSearchHelpers";
import {
  setLookUpHotelBrandId,
  brandSelector,
  brandsSelector,
  hotelBrandIdSelector,
} from "../../../../redux/slices/Brand/brand";
import {
  resetAmenities,
  searchSelector,
  setSearch,
} from "../../../../redux/slices/Search/search";
import {
  formatDate,
  parseSearchDate,
  searchDateFormat,
} from "../../../../services/dates";
import { gotoTab } from "../../../../utils/helpers";
import { Room } from "../../../../models/room";
import checkPromoCode from "../../../../services/crs/validatePromoCode";
import { useMobileDetectContext } from "../../../../context/MobileDetectContext";

const GlobalSearch: React.FC<IGlobalSearchProps> = ({
  location,
  isHotelPage = false,
  isSearchModal = false,
  onSearchSubmit = null,
  isBrandHotelSearchModal = false,
  locationFocus = false,
  onSubmit,
  isOfferModal = false,
  submitBtnText = "",
  isLogo = false,
  minLOS,
  minLeadDays,
}) => {
  const dispatch = useAppDispatch();
  const { isMobileOnly } = useMobileDetectContext();
  const search = useAppSelector(searchSelector);
  const brand = useAppSelector(brandSelector);
  const brands = useAppSelector(brandsSelector);
  const searchedHotelBrandId = useAppSelector(hotelBrandIdSelector);
  const [searchSuggestion, setSearchSuggesion] = useState();
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [searchCountry, setSearchCountry] = useState(
    search ? search.searchString : ""
  );
  const [autocompleteRef, setAutocompleteFocus] = useFocus();
  const [searchString, setSearchString] = useState(
    search ? search.searchString : ""
  );
  const [searchStringType, setSearchStringType] = useState<string | undefined>(
    search ? search.searchType : ""
  );
  const [searchTypeGapi, setsearchTypeGapi] = useState<
    string | undefined | null
  >(search ? search.searchTypeGapi : "");
  const [searchCityPath, setSearchCityPath] = useState("");
  const [latitude, setLatitude] = useState(search ? search.latitude : "");
  const [longitude, setLongitude] = useState(search ? search.longitude : "");
  const [searchToggled, setSearchToggled] = useState(false);
  const [updateMode, setUpdateMode] = useState(isHotelPage);
  const [searchStringBrandId, setSearchStringBrandId] = useState(
    searchedHotelBrandId || (brand ? brand.brand_id : "")
  );
  const [searchStringGooglePlaceId, setSearchStringGooglePlaceId] =
    useState("");
  const [isKeyInLocation, setIsKeyInLocation] = useState(false);
  const [showDiscounts, setShowDiscounts] = useState(false);
  const [showOccupancy, setShowOccupancy] = useState(false);
  const [showStayDates, setShowStayDates] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [searchStringStatus, setSearchStringStatus] = useState("Initial");
  const checkDateDifference = (searchDate: any, type: string) => {
    const searchedDate = new Date(searchDate);
    const currentDate = new Date();
    if (isAfter(currentDate, searchedDate)) {
      const validDate = type == "checkin" ? new Date() : addDays(new Date(), 1);
      return validDate;
    }
    return searchDate;
  };
  const [customUrl, setCustomUrl] = useState(search?.customUrl || "");
  const [checkin, setCheckin] = useState<Date | undefined | 0>(
    search && search.checkin
      ? checkDateDifference(parseSearchDate(search.checkin), "checkin")
      : new Date()
  );
  const [checkout, setCheckout] = useState<Date | undefined | 0>(
    search?.checkout
      ? checkDateDifference(parseSearchDate(search.checkout), "checkout")
      : addDays(new Date(), 1)
  );
  const [discount, setDiscount] = useState(search.discount);
  const [promotionCode, setPromotionCode] = useState(search.promotionCode);
  const [groupCode, setGroupCode] = useState(search.groupCode);
  const [rooms, setRooms] = useState(search.rooms);
  const [showPromoField, setShowPromoField] = useState(
    search.promotionCode !== null
  );
  const [showGroupField, setShowGroupField] = useState(
    search.groupCode !== null
  );
  const path = location.pathname;
  const pathParts = path.split("/");
  const isCheckout = pathParts[1] === "checkout";
  const isSearch = pathParts[1] === "hotel-search";
  const brandLinkPath = brand && brand.path && brand.path.alias;
  const isBrandSearch = brandLinkPath && brandLinkPath != "/";
  const isCityPage = pathParts[1] === "locations" && pathParts.length === 5;
  const isAirportPage = pathParts[1] === "locations" && pathParts.length === 6;
  const isPoiPage = pathParts[1] === "pois" && pathParts.length === 6;
  const locations = pathParts.includes("locations") && pathParts.length > 3;
  const isLRR = !discount && !showPromoField && !showGroupField;
  const [lrrSelected, setLrrSelected] = useState(
    Storage.GetLocalStorageValue("lrr_selected")
  );
  const [locationRequiredError, setLocationRequiredError] = useState("");

  useEffect(() => {
    setUpdateMode(
      isCityPage || isAirportPage || isPoiPage || isHotelPage || locations
    );
  }, []);

  // Update states if search object is updated from another component.
  useEffect(() => {
    setSearchString(search.searchString);
    setLatitude(search.latitude);
    setLongitude(search.longitude);
    setDiscount(search.discount);
    setPromotionCode(search.promotionCode);
    setGroupCode(search.groupCode);
    setSearchCountry(search.country);
    setCustomUrl(search.customUrl);
    setCheckin(checkDateDifference(parseSearchDate(search.checkin), "checkin"));
    setCheckout(
      checkDateDifference(parseSearchDate(search.checkout), "checkout")
    );
    setRooms(search.rooms);
    // Update show state if search updates programatically outside of form.
    setShowPromoField(search.promotionCode !== null);
    setShowGroupField(search.groupCode !== null);
  }, [search]);

  // Ensure valid dates on first render.
  useEffect(() => {
    const searchString =
      typeof location?.search === "string" ? location.search : "";
    let searchParams: URLSearchParams;

    try {
      searchParams = new URLSearchParams(searchString);
    } catch (error) {
      console.error("Invalid search string:", searchString, error);
      searchParams = new URLSearchParams(); // Fallback to an empty URLSearchParams object
    }

    const queryParams: Record<string, string> =
      Object.fromEntries(searchParams);
    const queryParamsLowercase = Object.keys(queryParams).reduce((qp, key) => {
      if (typeof key === "string" && typeof queryParams[key] === "string") {
        qp[key.toLowerCase()] = queryParams[key];
      }
      return qp;
    }, {} as Record<string, string>);

    const hasCheckinCheckoutParamsHotelpage =
      "checkin" in queryParamsLowercase &&
      "checkout" in queryParamsLowercase &&
      isHotelPage;
    if (!hasCheckinCheckoutParamsHotelpage) {
      const currentDate = startOfToday(); // new Date();
      const currentCheckin = new Date(parseSearchDate(search.checkin));
      if (isAfter(currentDate, currentCheckin)) {
        const updatedCheckin = currentDate;
        const updatedCheckout = addDays(currentDate, 1);
        setCheckin(updatedCheckin);
        setCheckout(updatedCheckout);

        if (search.checkin) {
          dispatch(
            setSearch({
              ...search,
              checkin: formatDate(updatedCheckin, searchDateFormat),
              checkout: formatDate(updatedCheckout, searchDateFormat),
            })
          );
        }
      }
    }
  }, []);

  useEffect(() => {
    if (locationFocus) {
      setAutocompleteFocus();
    }
  }, [locationFocus]);

  useEffect(() => {
    Storage.SetLocalStorageValue("lrr_selected", lrrSelected);
  }, [lrrSelected]);

  useEffect(() => {
    if (minLeadDays && checkin && isOfferModal && !isFormSubmitted) {
      const userSelectedLOS = moment(checkout).diff(moment(checkin), "days");
      // minimum required checkin date
      const minimumRequiredDate = moment(new Date())
        .add(Number(minLeadDays), "days")
        .toISOString();

      // check if the current date is less than the required date
      if (moment(checkin).isBefore(minimumRequiredDate)) {
        // check the number of lead days required to book this offer
        const diffInRequiredDays = moment(minimumRequiredDate).diff(
          moment(checkin),
          "days"
        );
        const updatedCheckinDate =
          moment(checkin)
            .add(Number(diffInRequiredDays), "days")
            .toISOString() || checkin;
        const updatedCheckoutDate =
          moment(updatedCheckinDate)
            .add(Number(userSelectedLOS), "days")
            .toISOString() || checkout;
        setCheckin(updatedCheckinDate);
        setCheckout(updatedCheckoutDate);
      }
    }
  }, [minLeadDays, isOfferModal, search]);

  useEffect(() => {
    if (minLOS && checkin && isOfferModal && !isFormSubmitted) {
      const endDate =
        moment(checkin).add(Number(minLOS), "days").toISOString() || checkout;
      setCheckout(endDate);
    }
  }, [minLOS, isOfferModal, search]);

  const setCoordinates = ({
    name,
    lat,
    lon,
    brandId,
    type,
    placeId,
    alias,
    suggestionObj,
    country,
    searchTypeGapi,
  }: ICoordinates) => {
    setSearchString(name);
    setSearchStringType(type);
    setsearchTypeGapi(searchTypeGapi);
    setSearchCityPath(alias ? alias : "");
    setCustomUrl(alias ? alias : "");
    setLatitude(lat);
    setLongitude(lon);
    setSearchToggled(false);
    setSearchCountry(country);
    if (
      isHotelPage ||
      isBrandSearch ||
      isCheckout ||
      isSearch ||
      isCityPage ||
      isAirportPage ||
      isPoiPage ||
      locations
    ) {
      setUpdateMode(name != "" && name === search.searchString);
    }
    setSearchStringBrandId(brandId);
    setSearchStringGooglePlaceId(placeId ? placeId : "");
    setLocationRequiredError("");
    suggestionObj && setSearchSuggesion(suggestionObj);
  };

  const handleToggleSearch = () => {
    setSearchToggled(!searchToggled);
    setSearchStringType("");
    setSearchCityPath("");
    setTimeout(setAutocompleteFocus, 0);
  };
  const saveSuggestionInRecentSearch = (suggestion: any) => {
    if (suggestion) {
      const recentSearches = Storage.GetLocalStorageValue(
        Constants.RECENT_SEARCH_KEY
      ) || [
        {
          type: "Recent Searches",
          data: [],
          class: "recent",
        },
      ];
      const recentSearchData = recentSearches[0]?.data;
      const existedSuggestionIndex = recentSearchData.findIndex(
        (d) => d.description === suggestion.description
      );
      existedSuggestionIndex != -1 &&
        recentSearchData.splice(existedSuggestionIndex, 1);
      recentSearchData.unshift({ ...suggestion, recent: true });

      if (recentSearchData.length > 3) {
        //save only 3 last recent searches
        recentSearchData.splice(3);
      }
      recentSearchData.findIndex((d) => d?.type === "clear") == -1 &&
        recentSearchData.push({
          type: "clear",
          description: "",
        });
      Storage.SetLocalStorageValue(Constants.RECENT_SEARCH_KEY, recentSearches);
    }
  };
  const handleToggleSearchKeyBrd = (_event: any) => {
    _event.key === "Enter" ? handleToggleSearch() : null;
  };

  const handleOnSearchLocationClick = () => {
    setShowDiscounts(false);
    setShowOccupancy(false);
    setShowStayDates(false);
  };

  const handleOnStayDatesClick = () => {
    setShowOccupancy(false);
    setShowDiscounts(false);
    setShowStayDates(true);
  };

  const handleCheckinChange = (
    checkin: React.SetStateAction<Date | undefined | 0>
  ) => {
    if (isDate(checkin)) {
      setCheckin(checkin);
      setCheckout(undefined);
    }
  };

  const handleCheckoutChange = (
    checkoutDate: React.SetStateAction<Date | undefined | 0>
  ) => {
    if (isDate(checkoutDate)) {
      setCheckout(checkoutDate);
    }
  };

  const handleSetOccupancy = (rooms: Room[]) => {
    setRooms(rooms);
  };

  const handleOccupancyDropdown = () => {
    setShowOccupancy(!showOccupancy);
    if (showDiscounts) {
      setShowDiscounts(false);
    }
  };

  const handleDiscountChange = (eventKey: any) => {
    const discountSelected =
      eventKey !== "LRR" && eventKey !== discount ? eventKey : null;
    setDiscount(discountSelected);
    setPromotionCode(null);
    setGroupCode(null);
    setShowPromoField(false);
    setShowGroupField(false);
    setShowDiscounts(false);

    setLrrSelected(eventKey === "LRR");
  };

  const handlePromotionCodeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = event.target.value.replace(/\s+/g, "");
    setPromotionCode(value.length ? value : null);
    if (event.target.value !== value) {
      event.target.value = value;
    }
    setGroupCode(null);
    setDiscount(null);
  };

  const handlePromotionCodeBlur = (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    const value = event.target.value.replace(/\s+/g, "");
    setPromotionCode(value.length ? value : null);
    if (event.target.value !== value) {
      event.target.value = value;
    }
  };

  const handleGroupCodeBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\s+/g, "");
    handlePromoCode(value.length ? value : null);
    if (event.target.value !== value) {
      event.target.value = value;
    }
  };

  const handleGroupCodeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = event.target.value.replace(/\s+/g, "");
    handlePromoCode(value.length ? value : null);
    if (event.target.value !== value) {
      event.target.value = value;
    }
  };

  const handlePromoCode = async (value: string) => {
    if (!value) {
      // If no value is provided, reset all relevant states.
      setPromotionCode(null);
      setGroupCode(null);
      setDiscount(null);
    } else {
      try {
        // Perform the promo code validation using the API.
        const response = await checkPromoCode(value.toUpperCase());

        // Check the HTTP status code to determine if the promo code is valid.
        if (response.status === 200) {
          // If the promo code is valid, set the promotionCode and reset groupCode.
          setPromotionCode(value);
          setGroupCode(null);
        } else {
          // If the promo code is not valid, reset promotionCode and set groupCode.
          setPromotionCode(null);
          setGroupCode(value);
        }
      } catch (error) {
        // Handle specific errors, such as when the API returns a 404.
        if (error.response && error.response.status === 404) {
          // Specifically handle the 404 case.
          setPromotionCode(null);
          setGroupCode(value);
        } else {
          // Log any other errors to the console.
          console.error("Error checking promo code:", error);
        }
        // Always reset discount when an error occurs.
        setDiscount(null);
      }
    }
  };

  const handleDiscountsDropdown = () => {
    if (promotionCode && promotionCode.trim().length < 1) {
      setPromotionCode(null);
      setShowPromoField(false);
    } else if (promotionCode === null && showPromoField) {
      setShowPromoField(false);
    }
    if (groupCode && groupCode.trim().length < 1) {
      setGroupCode(null);
      setShowGroupField(false);
    } else if (groupCode === null && showGroupField) {
      setShowGroupField(false);
    }
    setShowDiscounts(!showDiscounts);
    if (showOccupancy) {
      setShowOccupancy(false);
    }
    // document.getElementById("dropdown-discounts").focus();
    const element = document.getElementById("dropdown-discounts");
    if (element !== null) {
      element.focus();
    }
  };

  const handleShowPromo = () => {
    setShowPromoField(true);
    setDiscount(null);
    setGroupCode(null);
    setShowGroupField(false);
  };
  const handleShowGroup = () => {
    setShowGroupField(true);
    setDiscount(null);
    setPromotionCode(null);
    setShowPromoField(false);
  };

  const clearLocationRequiredError = () => {
    setLocationRequiredError("");
    setLocationError(false);
  };
  const handleSubmit = async (event: React.FormEvent<HTMLSelectElement>) => {
    dispatch(resetAmenities());
    setIsFormSubmitted(true);
    event.preventDefault();
    setShowDiscounts(false);
    setShowOccupancy(false);
    setShowStayDates(false);
    if (locationError) {
      setLocationRequiredError(
        "Turn on location services or enter a location."
      );
      return false;
    }
    if (!searchString || searchStringStatus === "Changed") {
      setLocationRequiredError("Please select a location.");
      return false;
    }
    const payload = {
      searchString: searchString.length ? searchString : search.searchString,
      searchType: searchStringType,
      customUrl: customUrl,
      searchTypeGapi: searchTypeGapi,
      latitude: latitude,
      longitude: longitude,
      country: searchCountry,
      distance: 50,
      checkin: formatDate(checkin, searchDateFormat),
      checkout: formatDate(checkout, searchDateFormat),
      rooms: rooms,
      discount: discount,
      promotionCode: promotionCode,
      groupCode: groupCode,
      ratePlanFilterCode: null,
      fromPage: window.location.pathname,
    };
    let searchedBrand = null;
    searchedBrand = brands?.filter((b: IBrand) => {
      return searchStringBrandId && searchStringBrandId == b.brand_id;
    })[0];
    searchedBrand && dispatch(setLookUpHotelBrandId(searchedBrand));
    await dispatch(setSearch(payload));
    saveSuggestionInRecentSearch(searchSuggestion);
    if (onSearchSubmit && onSearchSubmit instanceof Function) {
      onSearchSubmit(payload);
      if (isOfferModal && searchStringType == "hotel") {
        return true;
      }
    }

    if (searchString == "My Current Location") {
      navigate("/hotel-search", {
        state: {
          queryString: location.search,
        },
      });
    } else if (customUrl && location?.pathname !== "/checkout") {
      navigate(customUrl, {
        state: {
          queryString: location.search,
        },
      });
    } else if (
      (searchStringType === "city" ||
        searchStringType === "airport" ||
        searchStringType === "point_of_interest") &&
      searchCityPath
    ) {
      navigate(searchCityPath, {
        state: {
          queryString: location.search,
        },
      });
    } else if (
      (isBrandSearch && !updateMode) ||
      (isBrandSearch && brandLinkPath == path)
    ) {
      if (searchedBrand && searchedBrand.path.alias != brandLinkPath) {
        navigate(`${searchedBrand.path.alias}/hotel-search`, {
          state: {
            queryString: location.search,
          },
        });
      } else if (isHotelPage || isCheckout) {
        navigate(`/hotel-search`, {
          state: {
            queryString: location.search,
          },
        });
      } else {
        navigate(`${brandLinkPath}/hotel-search`, {
          state: {
            queryString: location.search,
          },
        });
      }
    } else if (
      (!isSearch &&
        !isCityPage &&
        !isAirportPage &&
        !isPoiPage &&
        !updateMode) ||
      (isSearchModal && !updateMode) ||
      (searchStringType &&
        !updateMode &&
        ["city", "airport", "point_of_interest", "hotel", "interest"].every(
          (value) => !searchStringType.includes(value)
        ))
    ) {
      navigate(`/hotel-search`, {
        state: {
          queryString: location.search,
        },
      });
    } else if (searchToggled) {
      setSearchToggled(false);
    }
    !searchToggled && onSubmit && onSubmit();
    gotoTab("rooms");
    gotoTab("searchResults");
  };

  const roomCount = rooms.length === 1 ? `1 Room` : `${rooms.length} Rooms`;
  let totalGuests = 0;
  rooms.forEach((room: { adults: any; children: any }) => {
    const roomGuests = room.adults + room.children;
    totalGuests = totalGuests + roomGuests;
  });
  const guestCount = totalGuests === 1 ? "1 Guest" : `${totalGuests} Guests`;
  let discountToggleText = lrrSelected ? LRR_LABEL : RATES_LABEL;
  if (discount !== null) {
    if (
      discount === "AAA" ||
      discount === "AAAB" ||
      discount === "AAAX" ||
      discount === "AARP"
    ) {
      discountToggleText = AUTO_LABEL;
    } else if (discount === "PKG") {
      discountToggleText = PACKAGE_LABEL;
    } else {
      discountToggleText = discount;
    }
  } else if (promotionCode && promotionCode.length) {
    discountToggleText = `${PROMO_LABEL}: ${promotionCode}`;
  } else if (groupCode !== null && groupCode.length) {
    discountToggleText = `${GROUP_LABEL}: ${groupCode}`;
  }

  const mobileDisplayClass = "d-block";

  const domNodeRef = useClickOutSide(() => {
    setShowStayDates(false);
  });
  const occuRef = useClickOutSide(() => {
    setShowOccupancy(false);
  });
  const discountRef = useClickOutSide(() => {
    setShowDiscounts(false);
  });
  if (showPromoField) {
    document.getElementById("corporate-promo")?.removeAttribute("href");
  }
  return (
    <SearchForm
      onSubmit={handleSubmit}
      isLogo={isLogo}
      isOfferModal={isOfferModal}
    >
      <Location
        extraclass={`${!isSearchModal ? "pr-lg-0 col-lg-3" : "col-lg-12"}`}
        is-ios={isIOS.toString()}
      >
        {!updateMode || searchToggled ? (
          <Form.Group className="mb-lg-0">
            <Form.Label
              id="global-search-autocomplete-label"
              htmlFor="global-search-autocomplete"
              srOnly
            >
              Location Search
            </Form.Label>
            <SearchAutoComplete
              inputRef={autocompleteRef}
              setCoordinates={setCoordinates}
              searchString={searchString}
              setLocationError={setLocationError}
              locationError={locationError}
              setSearchStringStatus={setSearchStringStatus}
              clearLocationRequiredError={clearLocationRequiredError}
              id="global-search-autocomplete"
              setIsKeyInLocation={setIsKeyInLocation}
              onSearchFocus={handleOnSearchLocationClick}
              error={locationRequiredError}
              isSearchModal={isSearchModal}
            />
          </Form.Group>
        ) : (
          <div className="d-flex hotel-name-container">
            <span
              tabIndex={updateMode ? 0 : -1}
              className="hotel-name overflow-hidden text-truncate mb-sm-1 mb-lg-0"
              onKeyPress={handleToggleSearchKeyBrd}
              onClick={handleToggleSearch}
            >
              {searchString}
            </span>
          </div>
        )}
      </Location>
      <Dates
        extraClass={`${
          !isSearchModal ? "pr-lg-0 col-lg-3" : "col-lg-12"
        } d-lg-block ${mobileDisplayClass} text-center dates-custom-class`}
        ref={domNodeRef}
        isLogo={isLogo}
      >
        <StayDates
          from={checkin}
          to={checkout}
          checkinDayChange={handleCheckinChange}
          checkoutDayChange={handleCheckoutChange}
          shouldHide={showOccupancy || showDiscounts || !showStayDates}
          onDateInputClick={handleOnStayDatesClick}
          isSearchModal={isSearchModal}
          isOfferModal={isOfferModal}
          minLOS={minLOS}
          minLeadDays={minLeadDays}
        />
      </Dates>
      <Col
        lg={isSearchModal ? 6 : 2}
        className={` d-lg-block ${
          !isSearchModal ? "search-occupancy pr-lg-0 custom-align" : ""
        } ${mobileDisplayClass}`}
        ref={occuRef}
      >
        <Form.Group className="mb-lg-0">
          <Form.Label
            htmlFor="dropdown-occupancy"
            id="dropdown-occupancy-label"
            srOnly
          >
            Select Room and Guest Count
          </Form.Label>
          <Dropdown className={`occupancy`} show={showOccupancy}>
            <Dropdown.Toggle
              variant="search-dropdown"
              className="form-control btn-search-dropdown"
              id="dropdown-occupancy"
              onClick={handleOccupancyDropdown}
              aria-labelledby="dropdown-occupancy-label"
            >
              <OccupancyText>{`${roomCount}, ${guestCount}`}</OccupancyText>
              <FontAwesomeIcon icon="chevron-down" />
            </Dropdown.Toggle>
            <Dropdown.Menu className={`occupancy`}>
              <GlobalOccupancy
                rooms={rooms}
                setOccupancy={handleSetOccupancy}
                // checkin={checkin && formatDate(checkin, searchDateFormat)}
                // checkout={checkout && formatDate(checkout, searchDateFormat)}
                searchString={searchString}
                // latitude={latitude}
                // longitude={longitude}
                placeId={searchStringGooglePlaceId}
                isSearchModal={isSearchModal}
                setOccupancyDropdown={handleOccupancyDropdown}
              />
            </Dropdown.Menu>
          </Dropdown>
        </Form.Group>
      </Col>
      <Col
        lg={isSearchModal ? 6 : 2}
        className={`d-lg-block search-discounts ${mobileDisplayClass}`}
        ref={discountRef}
      >
        <GlobalSearchForm
          discount={discount}
          promotionCode={promotionCode}
          groupCode={groupCode}
          showDiscounts={showDiscounts}
          isSearchModal={isSearchModal}
          handleDiscountsDropdown={handleDiscountsDropdown}
          isBrandHotelSearchModal={isBrandHotelSearchModal}
          discountToggleText={discountToggleText}
          handleDiscountChange={handleDiscountChange}
          isLRR={isLRR}
          handleShowPromo={handleShowPromo}
          showPromoField={showPromoField}
          handlePromotionCodeChange={handlePromotionCodeChange}
          handlePromotionCodeBlur={handlePromotionCodeBlur}
          handleShowGroup={handleShowGroup}
          showGroupField={showGroupField}
          handleGroupCodeChange={handleGroupCodeChange}
          handleGroupCodeBlur={handleGroupCodeBlur}
        />
      </Col>
      <Col
        lg={isSearchModal ? 12 : "auto"}
        className={`${
          !isSearchModal ? "pl-lg-0" : ""
        } d-lg-flex ${mobileDisplayClass}`}
      >
        <Button
          type="submit"
          variant="primary"
          className="mt-2 mt-lg-0 w-100"
          disabled={isKeyInLocation || !checkin || !checkout}
          id={"find-or-update-hotel"}
        >
          {submitBtnText
            ? submitBtnText
            : updateMode
            ? UPDATE_BUTTON_LABEL
            : FIND_HOTEL_BUTTON_LABEL}
        </Button>
      </Col>
    </SearchForm>
  );
};

export default GlobalSearch;
